import { useNavigate } from 'react-router-dom'
import { Flame } from 'lucide-react'
import type { Concept } from '@/types'
import DomainBadge from '@/components/shared/DomainBadge'
import { Button } from '@/components/ui/button'

interface HotConceptsProps {
  concepts: (Concept & { connection_count?: number })[]
}

export default function HotConcepts({ concepts }: HotConceptsProps) {
  const navigate = useNavigate()

  if (concepts.length === 0) {
    return null
  }

  // Sort by synapse_strength and take top 5
  const hotConcepts = [...concepts]
    .sort((a, b) => b.synapse_strength - a.synapse_strength)
    .slice(0, 5)

  const maxStrength = Math.max(...hotConcepts.map(c => c.synapse_strength))

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-400" />
          Conceitos Quentes
        </h3>
      </div>

      <div className="flex-1 space-y-3">
        {hotConcepts.map((concept) => {
          const percentage = Math.round((concept.synapse_strength / maxStrength) * 100)

          return (
            <button
              key={concept.id}
              onClick={() => navigate(`/dive/${concept.id}`)}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-white group-hover:text-rsb-accent transition-colors truncate">
                    {concept.name}
                  </span>
                  {concept.domain && (
                    <DomainBadge domain={concept.domain} size="sm" />
                  )}
                </div>
                <span className="text-xs font-mono text-white/60 ml-2">
                  {Math.round(concept.synapse_strength * 100)}%
                </span>
              </div>

              {/* Strength bar */}
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 group-hover:opacity-100"
                  style={{
                    width: `${percentage}%`,
                    background: `linear-gradient(90deg, #F97316 0%, #FB923C ${percentage}%)`,
                    opacity: 0.8,
                  }}
                />
              </div>
            </button>
          )
        })}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/constellation')}
        className="mt-4 w-full text-xs text-white/50 hover:text-white hover:bg-white/5"
      >
        Ver todos
      </Button>
    </div>
  )
}
